import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  query,
  orderBy,
  onSnapshot,
  getDocs
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import Header from '../Header';
import './AdminDashboard.css';

// AI Agent configurations - 32 AI players (one per piece).
// Nicknames are short human names (assigned per botId, stable across games).
// The piece role appears separately in chat (e.g. "Sam (White King)").
const AI_AGENTS = {
  // White pieces
  wk:  { email: 'ai-white-king@livingchess.local',    nickname: 'Sam',     piece: 'King' },
  wq:  { email: 'ai-white-queen@livingchess.local',   nickname: 'Alex',    piece: 'Queen' },
  wr1: { email: 'ai-white-rook1@livingchess.local',   nickname: 'Jordan',  piece: 'Rook' },
  wr2: { email: 'ai-white-rook2@livingchess.local',   nickname: 'Casey',   piece: 'Rook' },
  wb1: { email: 'ai-white-bishop1@livingchess.local', nickname: 'Riley',   piece: 'Bishop' },
  wb2: { email: 'ai-white-bishop2@livingchess.local', nickname: 'Avery',   piece: 'Bishop' },
  wn1: { email: 'ai-white-knight1@livingchess.local', nickname: 'Cameron', piece: 'Knight' },
  wn2: { email: 'ai-white-knight2@livingchess.local', nickname: 'Drew',    piece: 'Knight' },
  wp1: { email: 'ai-white-pawn1@livingchess.local',   nickname: 'Quinn',   piece: 'Pawn' },
  wp2: { email: 'ai-white-pawn2@livingchess.local',   nickname: 'Reese',   piece: 'Pawn' },
  wp3: { email: 'ai-white-pawn3@livingchess.local',   nickname: 'Skylar',  piece: 'Pawn' },
  wp4: { email: 'ai-white-pawn4@livingchess.local',   nickname: 'Hayden',  piece: 'Pawn' },
  wp5: { email: 'ai-white-pawn5@livingchess.local',   nickname: 'Morgan',  piece: 'Pawn' },
  wp6: { email: 'ai-white-pawn6@livingchess.local',   nickname: 'Parker',  piece: 'Pawn' },
  wp7: { email: 'ai-white-pawn7@livingchess.local',   nickname: 'Rowan',   piece: 'Pawn' },
  wp8: { email: 'ai-white-pawn8@livingchess.local',   nickname: 'Sage',    piece: 'Pawn' },
  // Black pieces
  bk:  { email: 'ai-black-king@livingchess.local',    nickname: 'Blake',   piece: 'King' },
  bq:  { email: 'ai-black-queen@livingchess.local',   nickname: 'Charlie', piece: 'Queen' },
  br1: { email: 'ai-black-rook1@livingchess.local',   nickname: 'Dakota',  piece: 'Rook' },
  br2: { email: 'ai-black-rook2@livingchess.local',   nickname: 'Ellis',   piece: 'Rook' },
  bb1: { email: 'ai-black-bishop1@livingchess.local', nickname: 'Emery',   piece: 'Bishop' },
  bb2: { email: 'ai-black-bishop2@livingchess.local', nickname: 'Finley',  piece: 'Bishop' },
  bn1: { email: 'ai-black-knight1@livingchess.local', nickname: 'Frankie', piece: 'Knight' },
  bn2: { email: 'ai-black-knight2@livingchess.local', nickname: 'Harper',  piece: 'Knight' },
  bp1: { email: 'ai-black-pawn1@livingchess.local',   nickname: 'Jamie',   piece: 'Pawn' },
  bp2: { email: 'ai-black-pawn2@livingchess.local',   nickname: 'Kai',     piece: 'Pawn' },
  bp3: { email: 'ai-black-pawn3@livingchess.local',   nickname: 'Logan',   piece: 'Pawn' },
  bp4: { email: 'ai-black-pawn4@livingchess.local',   nickname: 'Marley',  piece: 'Pawn' },
  bp5: { email: 'ai-black-pawn5@livingchess.local',   nickname: 'Micah',   piece: 'Pawn' },
  bp6: { email: 'ai-black-pawn6@livingchess.local',   nickname: 'Phoenix', piece: 'Pawn' },
  bp7: { email: 'ai-black-pawn7@livingchess.local',   nickname: 'River',   piece: 'Pawn' },
  bp8: { email: 'ai-black-pawn8@livingchess.local',   nickname: 'Tatum',   piece: 'Pawn' },
};

// Helper to get all AI emails
const getAllAIEmails = () => Object.values(AI_AGENTS).map(a => a.email);

// Maximum players per team (one per chess piece)
const TEAM_SIZE = 16;

// Game mode configurations with FEN positions
const GAME_MODES = {
  full: {
    name: 'Full Board',
    description: 'Standard chess starting position with all 32 pieces. Players control assigned pieces, unassigned pieces are not playable.',
    fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1'
  },
  midgame: {
    name: 'Mid-Game',
    description: 'Start from a balanced mid-game position with pieces already developed.',
    fen: 'r1bqkb1r/pppp1ppp/2n2n2/4p3/2B1P3/5N2/PPPP1PPP/RNBQK2R w KQkq - 4 4'
  },
  custom: {
    name: 'Custom FEN',
    description: 'Enter your own FEN string when you start the game (after teams are filled).',
    fen: null
  }
};

// Parse a FEN board into a pieces dictionary.
// IDs: king is always `${color}k`; other pieces are numbered in board-scan order
// (e.g. wq1, wr1, wr2, wp1...). This keeps assignRoles' type-prefix lookup working
// for any board, including custom positions with extra queens.
const parseFenToPieces = (fen) => {
  const pieces = {};
  const counts = {};
  const board = fen.split(' ')[0];
  const ranks = board.split('/');
  const typeMap = { k: 'king', q: 'queen', r: 'rook', b: 'bishop', n: 'knight', p: 'pawn' };

  ranks.forEach((rankStr, rankIdx) => {
    const rankNum = 8 - rankIdx;
    let fileIdx = 0;
    for (const ch of rankStr) {
      if ('12345678'.includes(ch)) {
        fileIdx += parseInt(ch, 10);
        continue;
      }
      const color = ch === ch.toUpperCase() ? 'white' : 'black';
      const colorPrefix = color === 'white' ? 'w' : 'b';
      const typePrefix = ch.toLowerCase();
      const type = typeMap[typePrefix];
      const file = String.fromCharCode('a'.charCodeAt(0) + fileIdx);
      const square = `${file}${rankNum}`;

      let id;
      if (typePrefix === 'k') {
        id = `${colorPrefix}k`;
      } else {
        const key = `${colorPrefix}${typePrefix}`;
        counts[key] = (counts[key] || 0) + 1;
        id = `${key}${counts[key]}`;
      }

      pieces[id] = {
        id,
        type,
        color,
        startPosition: square,
        currentPosition: square,
        captured: false,
        capturedBy: null
      };
      fileIdx += 1;
    }
  });

  return pieces;
};

const AdminDashboard = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newGameName, setNewGameName] = useState('');
  const [selectedGameMode, setSelectedGameMode] = useState('full');
  // Start-game FEN modal (only used when starting a Custom-mode game)
  const [gameToStart, setGameToStart] = useState(null);
  const [startFen, setStartFen] = useState('');
  const [startFenError, setStartFenError] = useState('');

  // Generate random invite code
  const generateInviteCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  // Real-time listener for games
  useEffect(() => {
    setLoading(true);
    const gamesRef = collection(db, 'games');
    const q = query(gamesRef, orderBy('createdAt', 'desc'));

    // Set up real-time listener
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const gamesData = [];
      querySnapshot.forEach((doc) => {
        gamesData.push({ id: doc.id, ...doc.data() });
      });

      setGames(gamesData);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching games:', error);
      setLoading(false);
    });

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, []);

  // Generate initial pieces data structure
  const generatePiecesData = () => {
    const pieces = {};

    // White pieces
    pieces['wk'] = { id: 'wk', type: 'king', color: 'white', startPosition: 'e1', currentPosition: 'e1', captured: false, capturedBy: null };
    pieces['wq'] = { id: 'wq', type: 'queen', color: 'white', startPosition: 'd1', currentPosition: 'd1', captured: false, capturedBy: null };
    pieces['wr1'] = { id: 'wr1', type: 'rook', color: 'white', startPosition: 'a1', currentPosition: 'a1', captured: false, capturedBy: null };
    pieces['wr2'] = { id: 'wr2', type: 'rook', color: 'white', startPosition: 'h1', currentPosition: 'h1', captured: false, capturedBy: null };
    pieces['wb1'] = { id: 'wb1', type: 'bishop', color: 'white', startPosition: 'c1', currentPosition: 'c1', captured: false, capturedBy: null };
    pieces['wb2'] = { id: 'wb2', type: 'bishop', color: 'white', startPosition: 'f1', currentPosition: 'f1', captured: false, capturedBy: null };
    pieces['wn1'] = { id: 'wn1', type: 'knight', color: 'white', startPosition: 'b1', currentPosition: 'b1', captured: false, capturedBy: null };
    pieces['wn2'] = { id: 'wn2', type: 'knight', color: 'white', startPosition: 'g1', currentPosition: 'g1', captured: false, capturedBy: null };
    // White pawns
    const whitePawnFiles = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
    whitePawnFiles.forEach((file, i) => {
      pieces[`wp${i + 1}`] = { id: `wp${i + 1}`, type: 'pawn', color: 'white', startPosition: `${file}2`, currentPosition: `${file}2`, captured: false, capturedBy: null };
    });

    // Black pieces
    pieces['bk'] = { id: 'bk', type: 'king', color: 'black', startPosition: 'e8', currentPosition: 'e8', captured: false, capturedBy: null };
    pieces['bq'] = { id: 'bq', type: 'queen', color: 'black', startPosition: 'd8', currentPosition: 'd8', captured: false, capturedBy: null };
    pieces['br1'] = { id: 'br1', type: 'rook', color: 'black', startPosition: 'a8', currentPosition: 'a8', captured: false, capturedBy: null };
    pieces['br2'] = { id: 'br2', type: 'rook', color: 'black', startPosition: 'h8', currentPosition: 'h8', captured: false, capturedBy: null };
    pieces['bb1'] = { id: 'bb1', type: 'bishop', color: 'black', startPosition: 'c8', currentPosition: 'c8', captured: false, capturedBy: null };
    pieces['bb2'] = { id: 'bb2', type: 'bishop', color: 'black', startPosition: 'f8', currentPosition: 'f8', captured: false, capturedBy: null };
    pieces['bn1'] = { id: 'bn1', type: 'knight', color: 'black', startPosition: 'b8', currentPosition: 'b8', captured: false, capturedBy: null };
    pieces['bn2'] = { id: 'bn2', type: 'knight', color: 'black', startPosition: 'g8', currentPosition: 'g8', captured: false, capturedBy: null };
    // Black pawns
    const blackPawnFiles = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
    blackPawnFiles.forEach((file, i) => {
      pieces[`bp${i + 1}`] = { id: `bp${i + 1}`, type: 'pawn', color: 'black', startPosition: `${file}7`, currentPosition: `${file}7`, captured: false, capturedBy: null };
    });

    return pieces;
  };

  // Validate FEN string (basic validation)
  const validateFen = (fen) => {
    if (!fen || !fen.trim()) {
      return 'FEN string is required';
    }
    const parts = fen.trim().split(' ');
    if (parts.length < 1) {
      return 'Invalid FEN format';
    }
    // Check board part has 8 ranks
    const ranks = parts[0].split('/');
    if (ranks.length !== 8) {
      return 'FEN must have 8 ranks separated by /';
    }
    // Check each rank has valid characters and sums to 8
    for (const rank of ranks) {
      let count = 0;
      for (const char of rank) {
        if ('12345678'.includes(char)) {
          count += parseInt(char);
        } else if ('kqrbnpKQRBNP'.includes(char)) {
          count += 1;
        } else {
          return `Invalid character '${char}' in FEN`;
        }
      }
      if (count !== 8) {
        return 'Each rank must have exactly 8 squares';
      }
    }
    // Check for both kings
    if (!fen.includes('K') || !fen.includes('k')) {
      return 'FEN must include both kings (K and k)';
    }
    return null; // Valid
  };

  // Create new game
  const handleCreateGame = async (e) => {
    e.preventDefault();

    if (!newGameName.trim()) {
      return;
    }

    try {
      const inviteCode = generateInviteCode();
      const gameMode = GAME_MODES[selectedGameMode];

      // Custom mode defers FEN entry to Start Game (so admin sees actual team sizes).
      // For other modes, use the mode's hardcoded FEN.
      const gameFen = selectedGameMode === 'custom'
        ? null
        : (gameMode.fen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1');

      const newGame = {
        name: newGameName,
        inviteCode: inviteCode,
        status: 'waiting', // waiting, in_progress, completed
        whiteTeam: [], // Array of {email, nickname, pieceId}
        blackTeam: [], // Array of {email, nickname, pieceId}
        pieces: generatePiecesData(), // All 32 pieces with unique IDs
        eliminatedPlayers: [], // Emails of eliminated players
        minPlayers: 2, // Minimum 2 players to start (1 per team - each becomes King)
        gameMode: selectedGameMode, // 'full', 'matched', 'midgame', or 'custom'
        createdBy: currentUser.email,
        createdAt: new Date().toISOString(),
        startedAt: null,
        completedAt: null,
        fen: gameFen,
        moveHistory: [],
        currentTurn: 'white',
        // Chats live in the /games/{gameId}/chats subcollection (hidden from spectators)
      };

      await addDoc(collection(db, 'games'), newGame);
      setNewGameName('');
      setShowCreateModal(false);
      console.log(`Game created! Invite code: ${inviteCode}`);
    } catch (error) {
      console.error('Error creating game:', error);
    }
  };

  // Fetch the /games/{gameId}/chats subcollection, grouped by team for readability.
  const fetchChatsForGame = async (gameId) => {
    const snap = await getDocs(
      query(collection(db, 'games', gameId, 'chats'), orderBy('timestamp', 'asc'))
    );
    const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const grouped = { white: [], black: [], general: [], other: [] };
    for (const msg of all) {
      if (msg.team === 'white') grouped.white.push(msg);
      else if (msg.team === 'black') grouped.black.push(msg);
      else if (msg.team === 'general') grouped.general.push(msg);
      else grouped.other.push(msg);
    }
    return { all, grouped };
  };

  const downloadJson = (filename, data) => {
    const dataStr = JSON.stringify(data, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Export single game (with its chats subcollection) as JSON
  const handleExportGame = async (game) => {
    try {
      const chats = await fetchChatsForGame(game.id);
      const exportData = {
        ...game,
        chats: chats.all,
        chatsByTeam: chats.grouped,
        exportedAt: new Date().toISOString(),
        exportedBy: currentUser.email
      };
      downloadJson(
        `living-chess-${game.name.replace(/\s+/g, '-')}-${game.id}.json`,
        exportData
      );
      console.log(`Game "${game.name}" exported successfully (${chats.all.length} chat messages)`);
    } catch (error) {
      console.error('Error exporting game:', error);
    }
  };

  // Export all games (with their chats subcollections) as one JSON
  const handleExportAllGames = async () => {
    try {
      const enriched = await Promise.all(
        games.map(async (game) => {
          const chats = await fetchChatsForGame(game.id);
          return {
            ...game,
            chats: chats.all,
            chatsByTeam: chats.grouped,
            exportedAt: new Date().toISOString()
          };
        })
      );

      const exportData = {
        games: enriched,
        exportedAt: new Date().toISOString(),
        exportedBy: currentUser.email,
        totalGames: enriched.length
      };

      downloadJson(
        `living-chess-all-games-${new Date().toISOString().split('T')[0]}.json`,
        exportData
      );
      console.log(`All ${games.length} games exported successfully`);
    } catch (error) {
      console.error('Error exporting all games:', error);
    }
  };

  // Delete game directly without archiving or confirmation
  const handleDeleteGame = async (gameId, gameName) => {
    try {
      await deleteDoc(doc(db, 'games', gameId));
      console.log(`Game "${gameName}" deleted`);
    } catch (error) {
      console.error('Error deleting game:', error);
    }
  };

  // Assign pieces to players - each player gets ONE piece ID
  // gameMode parameter determines which pieces are available
  const assignRoles = (team, color, pieces, gameMode = 'full') => {
    const prefix = color === 'white' ? 'w' : 'b';

    // Get available piece IDs for this color from the pieces object
    // This handles both full mode (all 16 pieces) and minimal mode (fewer pieces)
    const availablePieceIds = Object.keys(pieces).filter(id =>
      id.startsWith(prefix) && !pieces[id].captured
    );

    // Sort to ensure King is first, then by piece value
    const pieceOrder = { k: 0, q: 1, r: 2, b: 3, n: 4, p: 5 };
    availablePieceIds.sort((a, b) => {
      const typeA = a.charAt(1);
      const typeB = b.charAt(1);
      return (pieceOrder[typeA] ?? 99) - (pieceOrder[typeB] ?? 99);
    });

    const kingId = `${prefix}k`;

    // AI bots have a piece encoded in their email — preserve that mapping so
    // "King Arthur" actually plays the king, not a random pawn. Build email → pieceId.
    const aiEmailToPieceId = {};
    Object.entries(AI_AGENTS).forEach(([pieceId, agent]) => {
      aiEmailToPieceId[agent.email] = pieceId;
    });

    const updatedPieces = { ...pieces };
    const assignments = new Map(); // playerEmail → pieceId
    const claimedPieceIds = new Set();

    const isAI = (player) => player.email?.endsWith('@livingchess.local');
    const humans = team.filter(p => !isAI(p));
    const aiBots = team.filter(isAI);

    // Pass 1: humans claim pieces in king-first order, in join order.
    // This guarantees the king goes to a human if any human is on the team.
    const humanPieceQueue = [...availablePieceIds]; // already sorted king-first
    humans.forEach(player => {
      const pieceId = humanPieceQueue.shift();
      if (pieceId) {
        assignments.set(player.email, pieceId);
        claimedPieceIds.add(pieceId);
      }
    });

    // Pass 2: AI bots claim their intrinsic piece if still available.
    aiBots.forEach(player => {
      const intrinsic = aiEmailToPieceId[player.email];
      if (intrinsic && availablePieceIds.includes(intrinsic) && !claimedPieceIds.has(intrinsic)) {
        assignments.set(player.email, intrinsic);
        claimedPieceIds.add(intrinsic);
      }
    });

    // Pass 3: AI bots without their intrinsic piece get leftovers (shuffled).
    const leftoverPieceIds = availablePieceIds.filter(id => !claimedPieceIds.has(id));
    for (let i = leftoverPieceIds.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [leftoverPieceIds[i], leftoverPieceIds[j]] = [leftoverPieceIds[j], leftoverPieceIds[i]];
    }
    const unassignedAI = aiBots.filter(p => !assignments.has(p.email));
    unassignedAI.forEach((player, idx) => {
      assignments.set(player.email, leftoverPieceIds[idx] || null);
    });

    // Build the final team array, preserving original join order
    const updatedTeam = team.map(player => {
      const assignedPieceId = assignments.get(player.email) || null;

      // Update piece to track which player controls it
      if (assignedPieceId && updatedPieces[assignedPieceId]) {
        updatedPieces[assignedPieceId] = {
          ...updatedPieces[assignedPieceId],
          playerEmail: player.email,
          playerNickname: player.nickname
        };
      }

      // Generate display role from piece data
      let displayRole = 'Supporter';
      if (assignedPieceId && pieces[assignedPieceId]) {
        const piece = pieces[assignedPieceId];
        const typeName = piece.type.charAt(0).toUpperCase() + piece.type.slice(1);
        const colorName = piece.color.charAt(0).toUpperCase() + piece.color.slice(1);
        displayRole = `${colorName} ${typeName} (${piece.startPosition.toUpperCase()})`;
      }

      return {
        ...player,
        pieceId: assignedPieceId,
        role: displayRole // Keep role for display purposes
      };
    });

    return { updatedTeam, updatedPieces };
  };

  // Start game. For custom-mode games, opens a FEN-entry modal first.
  const handleStartGame = async (game) => {
    const totalPlayers = game.whiteTeam.length + game.blackTeam.length;

    if (totalPlayers < game.minPlayers) {
      console.log(`Need at least ${game.minPlayers} players to start the game. Currently have ${totalPlayers} players.`);
      return;
    }

    if (game.whiteTeam.length === 0 || game.blackTeam.length === 0) {
      console.log('Each team must have at least 1 player.');
      return;
    }

    const gameMode = game.gameMode || 'full';
    if (gameMode === 'custom' && !game.fen) {
      setGameToStart(game);
      setStartFen('');
      setStartFenError('');
      return;
    }

    await finalizeStartGame(game, game.fen);
  };

  // Confirm-start handler used by the Custom-FEN start modal.
  const handleConfirmStartGame = async (e) => {
    e.preventDefault();
    if (!gameToStart) return;
    const error = validateFen(startFen);
    if (error) {
      setStartFenError(error);
      return;
    }
    const game = gameToStart;
    setGameToStart(null);
    setStartFen('');
    setStartFenError('');
    await finalizeStartGame(game, startFen.trim());
  };

  // Shared logic: derive pieces from FEN, assign roles, write to Firestore.
  const finalizeStartGame = async (game, fen) => {
    try {
      const gameRef = doc(db, 'games', game.id);
      const gameMode = game.gameMode || 'full';

      // For custom mode, derive pieces from the FEN so the dict matches the board.
      // Other modes use the standard 32-piece dict (already saved at creation).
      let pieces = gameMode === 'custom'
        ? parseFenToPieces(fen)
        : (game.pieces || generatePiecesData());

      const whiteResult = assignRoles(game.whiteTeam, 'white', pieces, gameMode);
      pieces = whiteResult.updatedPieces;

      const blackResult = assignRoles(game.blackTeam, 'black', pieces, gameMode);
      pieces = blackResult.updatedPieces;

      await updateDoc(gameRef, {
        status: 'in_progress',
        startedAt: new Date().toISOString(),
        whiteTeam: whiteResult.updatedTeam,
        blackTeam: blackResult.updatedTeam,
        pieces,
        fen
      });

      console.log('Game started! Pieces assigned to all players.');
    } catch (error) {
      console.error('Error starting game:', error);
    }
  };

  // Regenerate invite code
  const handleRegenerateCode = async (gameId) => {
    const newCode = generateInviteCode();
    try {
      const gameRef = doc(db, 'games', gameId);
      await updateDoc(gameRef, {
        inviteCode: newCode
      });
      console.log(`New invite code: ${newCode}`);
    } catch (error) {
      console.error('Error regenerating code:', error);
    }
  };

  // Fill empty slots on a team with AI bots, capped at 16 total players per team
  const handleAddAllAIPlayers = async (game, team) => {
    const teamField = team === 'white' ? 'whiteTeam' : 'blackTeam';
    const prefix = team === 'white' ? 'w' : 'b';
    const currentTeam = team === 'white' ? game.whiteTeam : game.blackTeam;
    const currentList = currentTeam || [];
    const slotsLeft = TEAM_SIZE - currentList.length;

    if (slotsLeft <= 0) {
      console.log(`${team} team is already full (${currentList.length}/${TEAM_SIZE})`);
      return;
    }

    // AI agents available for this color, excluding ones already on the team.
    // King is first in AI_AGENTS insertion order so it's preferred when slots are tight.
    const existingEmails = new Set(currentList.map(p => p.email));
    const availableAgents = Object.entries(AI_AGENTS)
      .filter(([pieceId]) => pieceId.startsWith(prefix))
      .map(([, agent]) => agent)
      .filter(agent => !existingEmails.has(agent.email));

    const newAIPlayers = availableAgents.slice(0, slotsLeft).map(agent => ({
      email: agent.email,
      nickname: agent.nickname,
      isAI: true,
      joinedAt: new Date().toISOString()
    }));

    if (newAIPlayers.length === 0) {
      console.log('No AI agents left to add to this team');
      return;
    }

    try {
      const gameRef = doc(db, 'games', game.id);

      // Add all AI players at once
      const updatedTeam = [...(currentTeam || []), ...newAIPlayers];

      await updateDoc(gameRef, {
        [teamField]: updatedTeam
      });

      console.log(`Added ${newAIPlayers.length} AI players to ${team} team`);
    } catch (error) {
      console.error('Error adding AI players:', error);
    }
  };

  // Remove all AI players from a team
  const handleRemoveAllAIPlayers = async (game, team) => {
    const teamField = team === 'white' ? 'whiteTeam' : 'blackTeam';
    const currentTeam = team === 'white' ? game.whiteTeam : game.blackTeam;

    if (!currentTeam || currentTeam.length === 0) {
      return;
    }

    // Filter out all AI players
    const allAIEmails = getAllAIEmails();
    const humanPlayers = currentTeam.filter(p => !allAIEmails.includes(p.email));

    try {
      const gameRef = doc(db, 'games', game.id);
      await updateDoc(gameRef, {
        [teamField]: humanPlayers
      });

      const removedCount = currentTeam.length - humanPlayers.length;
      console.log(`Removed ${removedCount} AI players from ${team} team`);
    } catch (error) {
      console.error('Error removing AI players:', error);
    }
  };

  // Count AI players on a team
  const countAIOnTeam = (game, team) => {
    const teamData = team === 'white' ? game.whiteTeam : game.blackTeam;
    const allAIEmails = getAllAIEmails();
    return teamData?.filter(p => allAIEmails.includes(p.email)).length || 0;
  };

  // Empty slots on a team (capped at TEAM_SIZE players per team)
  const emptySlotsOnTeam = (game, team) => {
    const teamData = team === 'white' ? game.whiteTeam : game.blackTeam;
    return Math.max(0, TEAM_SIZE - (teamData?.length || 0));
  };

  const getStatusBadge = (status) => {
    const badges = {
      waiting: { text: 'Waiting for Players', class: 'status-waiting' },
      in_progress: { text: 'In Progress', class: 'status-active' },
      completed: { text: 'Completed', class: 'status-completed' }
    };
    return badges[status] || badges.waiting;
  };

  return (
    <div className="admin-container">
      <Header />

      <div className="admin-content">
        <div className="content-header">
          <h2 className="section-title">Game Management</h2>
          <div className="header-buttons">
            <button
              onClick={() => navigate('/admin/users')}
              className="manage-users-button"
            >
              Manage Users
            </button>
            {games.length > 0 && (
              <button
                onClick={handleExportAllGames}
                className="export-all-button"
                title="Export all games as JSON"
              >
                Export All Games
              </button>
            )}
            <button
              onClick={() => setShowCreateModal(true)}
              className="create-game-button"
            >
              + Create New Game
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading games...</p>
          </div>
        ) : games.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">♔</div>
            <h3>No games yet</h3>
            <p>Create your first game to get started</p>
          </div>
        ) : (
          <div className="games-grid">
            {games.map((game) => {
              const statusInfo = getStatusBadge(game.status);
              return (
                <div key={game.id} className="game-card">
                  <div className="game-card-header">
                    <h3 className="game-name">{game.name}</h3>
                    <span className={`status-badge ${statusInfo.class}`}>
                      {statusInfo.text}
                    </span>
                  </div>

                  <div className="game-info">
                    <div className="info-row">
                      <span className="info-label">Invite Code:</span>
                      <div className="invite-code-container">
                        <span className="invite-code">{game.inviteCode}</span>
                        <button
                          onClick={() => handleRegenerateCode(game.id)}
                          className="regenerate-button"
                          title="Regenerate code"
                        >
                          ↻
                        </button>
                      </div>
                    </div>

                    <div className="info-row">
                      <span className="info-label">Share Link:</span>
                      <div className="share-link-container">
                        <input
                          type="text"
                          value={`${window.location.origin}/join/${game.inviteCode}`}
                          readOnly
                          className="share-link-input"
                          onClick={(e) => e.target.select()}
                        />
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/join/${game.inviteCode}`);
                            alert('Link copied to clipboard!');
                          }}
                          className="copy-link-button"
                          title="Copy link"
                        >
                          📋
                        </button>
                      </div>
                    </div>

                    <div className="teams-section">
                      <div className="team-box">
                        <div className="team-header">
                          <span className="team-icon-small">♔</span>
                          <span className="team-title">White Team ({game.whiteTeam?.length || 0})</span>
                        </div>
                        <div className="team-players">
                          {game.whiteTeam && game.whiteTeam.length > 0 ? (
                            game.whiteTeam.map((player, index) => (
                              <div key={index} className={`player-item ${player.isAI ? 'ai-player' : ''}`}>
                                <span className="player-nickname">
                                  {player.isAI && <span className="ai-badge">AI</span>}
                                  {player.nickname}
                                </span>
                                {player.role && (
                                  <span className="player-role">{player.role}</span>
                                )}
                              </div>
                            ))
                          ) : (
                            <span className="no-players">No players yet</span>
                          )}
                        </div>
                        {game.status === 'waiting' && (
                          <div className="ai-controls">
                            {countAIOnTeam(game, 'white') > 0 && (
                              <button
                                className="ai-button remove-ai"
                                onClick={() => handleRemoveAllAIPlayers(game, 'white')}
                                title="Remove all AI from White team"
                              >
                                Remove AI ({countAIOnTeam(game, 'white')})
                              </button>
                            )}
                            {emptySlotsOnTeam(game, 'white') > 0 && (
                              <button
                                className="ai-button add-ai"
                                onClick={() => handleAddAllAIPlayers(game, 'white')}
                                title={`Fill remaining ${emptySlotsOnTeam(game, 'white')} slot(s) on White team with AI bots`}
                              >
                                + Fill with AI ({emptySlotsOnTeam(game, 'white')})
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="team-box">
                        <div className="team-header">
                          <span className="team-icon-small">♚</span>
                          <span className="team-title">Black Team ({game.blackTeam?.length || 0})</span>
                        </div>
                        <div className="team-players">
                          {game.blackTeam && game.blackTeam.length > 0 ? (
                            game.blackTeam.map((player, index) => (
                              <div key={index} className={`player-item ${player.isAI ? 'ai-player' : ''}`}>
                                <span className="player-nickname">
                                  {player.isAI && <span className="ai-badge">AI</span>}
                                  {player.nickname}
                                </span>
                                {player.role && (
                                  <span className="player-role">{player.role}</span>
                                )}
                              </div>
                            ))
                          ) : (
                            <span className="no-players">No players yet</span>
                          )}
                        </div>
                        {game.status === 'waiting' && (
                          <div className="ai-controls">
                            {countAIOnTeam(game, 'black') > 0 && (
                              <button
                                className="ai-button remove-ai"
                                onClick={() => handleRemoveAllAIPlayers(game, 'black')}
                                title="Remove all AI from Black team"
                              >
                                Remove AI ({countAIOnTeam(game, 'black')})
                              </button>
                            )}
                            {emptySlotsOnTeam(game, 'black') > 0 && (
                              <button
                                className="ai-button add-ai"
                                onClick={() => handleAddAllAIPlayers(game, 'black')}
                                title={`Fill remaining ${emptySlotsOnTeam(game, 'black')} slot(s) on Black team with AI bots`}
                              >
                                + Fill with AI ({emptySlotsOnTeam(game, 'black')})
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="game-meta">
                      <div className="meta-item">
                        <span className="meta-label">Total Players:</span>
                        <span className="meta-value">{(game.whiteTeam?.length || 0) + (game.blackTeam?.length || 0)} / {game.minPlayers}</span>
                      </div>
                      <div className="meta-item">
                        <span className="meta-label">Created:</span>
                        <span className="meta-value">{new Date(game.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>

                  <div className="game-actions">
                    {game.status === 'waiting' && (
                      <button
                        onClick={() => handleStartGame(game)}
                        className="action-button start-button"
                        disabled={(game.whiteTeam?.length || 0) + (game.blackTeam?.length || 0) < game.minPlayers}
                      >
                        Start Game
                      </button>
                    )}
                    <button
                      onClick={() => window.location.href = `/game/${game.id}`}
                      className="action-button view-button"
                    >
                      View Game
                    </button>
                    <button
                      onClick={() => handleExportGame(game)}
                      className="action-button export-button"
                      title="Download game data as JSON"
                    >
                      Export
                    </button>
                    <button
                      onClick={() => handleDeleteGame(game.id, game.name)}
                      className="action-button delete-button"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Game Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Game</h2>
              <button
                className="close-button"
                onClick={() => setShowCreateModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateGame} className="modal-form">
              <div className="form-group">
                <label htmlFor="gameName">Game Name</label>
                <input
                  type="text"
                  id="gameName"
                  value={newGameName}
                  onChange={(e) => setNewGameName(e.target.value)}
                  placeholder="e.g., Tournament Round 1"
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Game Mode</label>
                <div className="game-mode-options">
                  {Object.entries(GAME_MODES).map(([key, mode]) => (
                    <div
                      key={key}
                      className={`game-mode-option ${selectedGameMode === key ? 'selected' : ''}`}
                      onClick={() => setSelectedGameMode(key)}
                    >
                      <div className="game-mode-name">{mode.name}</div>
                      <div className="game-mode-description">{mode.description}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="cancel-button"
                >
                  Cancel
                </button>
                <button type="submit" className="submit-button">
                  Create Game
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Start Game Modal — Custom FEN entry, only shown for custom-mode games */}
      {gameToStart && (
        <div className="modal-overlay" onClick={() => setGameToStart(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Start Game: {gameToStart.name}</h2>
              <button
                className="close-button"
                onClick={() => setGameToStart(null)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleConfirmStartGame} className="modal-form">
              <div className="form-group">
                <label>Teams</label>
                <div className="help-text">
                  White: {gameToStart.whiteTeam?.length || 0} player(s) · Black: {gameToStart.blackTeam?.length || 0} player(s)
                  <br />
                  Make sure your FEN has at least that many pieces per side. Each player gets one piece (king first, then queen, rook, bishop, knight, pawn). Extra pieces become unassigned.
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="startFen">FEN String</label>
                <input
                  type="text"
                  id="startFen"
                  value={startFen}
                  onChange={(e) => {
                    setStartFen(e.target.value);
                    setStartFenError('');
                  }}
                  placeholder="e.g., rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"
                  className={startFenError ? 'input-error' : ''}
                  autoFocus
                />
                {startFenError && <span className="fen-error">{startFenError}</span>}
                <span className="help-text">
                  Enter a valid FEN. You can build positions on lichess.org/editor and copy the FEN.
                </span>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setGameToStart(null)}
                  className="cancel-button"
                >
                  Cancel
                </button>
                <button type="submit" className="submit-button">
                  Start Game
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
